import React, { memo } from 'react';
import { AlertCircle, UserCheck, Stethoscope, Briefcase, Scale } from 'lucide-react';
import { motion } from 'framer-motion';
export function PainPoints() {
  const pains = [{
    title: 'Cada primera consulta la armás distinta',
    desc: 'Sin anamnesis estructurada, cada evaluación depende de tu memoria del día.'
  }, {
    title: 'Los padres te desbordan',
    desc: 'No sabés cómo encuadrar la relación con el adulto que decide y paga.'
  }, {
    title: 'Escribís cada informe desde cero',
    desc: 'Horas perdidas redactando para la escuela, la obra social o el juzgado.'
  }, {
    title: 'Consentimientos improvisados',
    desc: 'Padres separados, patria potestad compartida, secreto profesional — zona gris que te genera miedo a la exposición legal.'
  }, {
    title: 'Protocolos que no tenés a mano',
    desc: 'Ansiedad, depresión, trauma: cada caso te obliga a rearmar el abordaje.'
  }, {
    title: 'Cero registro de evolución',
    desc: 'No podés mostrarle a los padres (ni a vos) que el tratamiento está funcionando.'
  }, {
    title: 'La escuela no te entiende',
    desc: 'Perdés tiempo traduciendo lenguaje clínico para que un docente lo pueda aplicar.'
  }, {
    title: 'Domingo a la noche, angustia de lunes',
    desc: 'Sabés que tenés 3 primeras consultas nuevas y no tenés el material listo.'
  }];
  const targets = [{
    icon: <Stethoscope className="w-6 h-6 text-blue-600" data-id="element-107" />,
    title: 'Psicólogas/os en consultorio propio',
    desc: 'Atención particular o con obra social, buscando estructura profesional.'
  }, {
    icon: <UserCheck className="w-6 h-6 text-blue-600" data-id="element-108" />,
    title: 'Recién egresados/as',
    desc: 'Formación clínica sólida, cero estructura administrativa todavía.'
  }, {
    icon: <Briefcase className="w-6 h-6 text-blue-600" data-id="element-109" />,
    title: 'Profesionales con consulta desbordada',
    desc: 'Necesitás sistematizar para atender más sin perder calidad.'
  }, {
    icon: <Scale className="w-6 h-6 text-blue-600" data-id="element-110" />,
    title: 'Derivaciones judiciales o escolares',
    desc: 'Necesitás informes y consentimientos que te protejan legalmente.'
  }];
  return <section className="py-10 md:py-20 bg-white" data-id="element-111">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-112">
        {/* Pain Points */}
        <div className="mb-16 md:mb-24" data-id="element-113">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16" data-id="element-114">
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-3 md:mb-4" data-id="element-115">
              ¿TE PASA ESTO?
            </h2>
            <p className="text-base md:text-lg text-slate-600" data-id="element-116">
              Si atendés psicología infanto-juvenil en consultorio privado, esto
              te suena conocido:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" data-id="element-117">
            {pains.map((pain, i) => <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} whileHover={{
            y: -4,
            shadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)'
          }} key={i} className="bg-white p-5 md:p-6 rounded-2xl shadow-sm border border-slate-100 transition-all cursor-default" data-id="element-118">
                <AlertCircle className="w-6 h-6 md:w-8 md:h-8 text-red-500 mb-3 md:mb-4" data-id="element-119" />
                <h3 className="font-bold text-slate-900 mb-1 md:mb-2 leading-tight text-sm md:text-base" data-id="element-120">
                  {pain.title}
                </h3>
                <p className="text-slate-600 text-xs md:text-sm leading-relaxed" data-id="element-121">
                  {pain.desc}
                </p>
              </motion.div>)}
          </div>

          <div className="mt-8 md:mt-12 text-center" data-id="element-122">
            <p className="text-sm md:text-xl font-medium text-slate-800 bg-blue-50 inline-block px-4 py-2 md:px-6 md:py-3 rounded-xl" data-id="element-123">
              ¿Cuántas semanas más vas a armar cada consulta desde cero?
            </p>
          </div>
        </div>

        {/* Target Audience */}
        <div data-id="element-124">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16" data-id="element-125">
            <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-3 md:mb-4" data-id="element-126">
              ¿PARA QUIÉN ES?
            </h2>
            <p className="text-base md:text-lg text-slate-600" data-id="element-127">
              Diseñado para vos si atendés infancia y adolescencia en
              consultorio privado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-4xl mx-auto" data-id="element-128">
            {targets.map((target, i) => <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} whileHover={{
            scale: 1.02
          }} key={i} className="flex gap-3 md:gap-4 bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-slate-100 cursor-default" data-id="element-129">
                <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-blue-50 rounded-xl flex items-center justify-center" data-id="element-130">
                  {target.icon}
                </div>
                <div data-id="element-131">
                  <h3 className="font-bold text-slate-900 mb-1 text-sm md:text-base" data-id="element-132">
                    {target.title}
                  </h3>
                  <p className="text-slate-600 text-xs md:text-sm" data-id="element-133">
                    {target.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>

          <div className="mt-8 md:mt-10 text-center" data-id="element-134">
            <a href="#oferta" className="text-blue-600 font-semibold hover:text-blue-700 inline-flex items-center gap-1 text-sm md:text-base" data-id="element-135">
              ¿No estás segura/o si es para vos? Tenés 7 días de garantía →
            </a>
          </div>
        </div>
      </div>
    </section>;
}